import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Mail, Lock, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import LogoHorizontal from '../components/LogoHorizontal';
import { safeReturnPath } from '../lib/authReturn';

// Helper to format API errors
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Algo salió mal. Por favor, inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Roles without a dashboard land directly on /activos to avoid the
  // ProtectedRoute redirect ping-pong (mediador/inversor have no /dashboard).
  const _landingFor = (u) => {
    const r = (u?.role || '').toString().trim().toLowerCase();
    if (r === 'mediador' || r === 'inversor') return '/activos';
    return '/dashboard';
  };

  // (Feb 2026 session-expired) Si el login se abrió con `?redirect=<path>`,
  // devolvemos al usuario a esa misma pantalla tras autenticarse. Sólo se
  // aceptan paths relativos que empiezan por `/` para evitar open redirect.
  const _postLoginTarget = (u) => {
    const params = new URLSearchParams(location.search);
    const rd = params.get('redirect') || '';
    const from = location.state?.from;
    const target = safeReturnPath(rd) || safeReturnPath(typeof from === 'string' ? from : from ? from.pathname + (from.search || '') + (from.hash || '') : '');
    if (target) return target;
    return _landingFor(u);
  };

  useEffect(() => {
    if (!authLoading && user) {
      navigate(_postLoginTarget(user), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        result = await register(name, email, password);
      }
      navigate(_postLoginTarget(result || user), { replace: true });
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const target = _postLoginTarget(null);
    if (safeReturnPath(target)) sessionStorage.setItem('auth-return-path', target);
    const sharedPath = new URLSearchParams(location.search).get('redirect') || '';
    if (/^\/a\/[A-Za-z0-9_-]{22}$/.test(sharedPath)) sessionStorage.setItem('asset-share-return', sharedPath);
    else sessionStorage.removeItem('asset-share-return');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-primary)' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-blue-accent)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--color-bg-primary)' }} data-testid="login-page">
      
      {/* Left side - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0B0F1A 0%, #14213D 60%, #1E3A8A 100%)',
        }}
      >
        {/* Decorative glow */}
        <div
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.18), transparent 60%)' }}
        />
        <div
          className="absolute -bottom-32 -left-20 w-[28rem] h-[28rem] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.22), transparent 65%)' }}
        />

        <div className="flex items-center gap-3 relative z-10">
          <LogoHorizontal height={160} textColor="#FFFFFF" fontWeight={600} />
        </div>
        
        <div className="space-y-6 relative z-10">
          <h1 className="text-4xl font-semibold text-white leading-tight tracking-tight">
            Convierte oportunidades<br />en rentabilidad real
          </h1>
          <p className="text-lg max-w-md" style={{ color: '#F1F5F9' }}>
            Inteligencia operativa para inversores que buscan ventaja en activos complejos. Analiza, decide y ejecuta antes que el mercado.
          </p>
        </div>

        <div className="flex gap-8 text-sm relative z-10" style={{ color: '#CBD5E1' }}>
          <div>
            <p className="text-3xl font-semibold text-white">100%</p>
            <p>Digitalizado</p>
          </div>
          <div>
            <p className="text-3xl font-semibold text-white">ROI</p>
            <p>Automático</p>
          </div>
          <div>
            <p className="text-3xl font-semibold" style={{ color: 'var(--color-blue-accent)' }}>IA</p>
            <p>Valoración</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8" style={{ background: 'var(--color-bg-primary)' }}>
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <LogoHorizontal height={112} />
          </div>

          <div className="text-center lg:text-left">
            <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {isLogin ? 'Iniciar sesión' : 'Crear cuenta'}
            </h2>
            <p className="mt-2" style={{ color: 'var(--color-text-muted)' }}>
              {isLogin 
                ? 'Accede a tu cuenta' 
                : 'Crea tu cuenta para empezar'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <label htmlFor="name" style={{ color: 'var(--color-text-secondary)' }}>Nombre</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre"
                    className="pl-10 input-base"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" style={{ color: 'var(--color-text-secondary)' }}>Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="pl-10 input-base"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" style={{ color: 'var(--color-text-secondary)' }}>Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 input-base"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="w-full btn-primary h-11"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                isLogin ? 'Iniciar sesión' : 'Crear cuenta'
              )}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full" style={{ borderTop: '1px solid var(--color-border)' }} />
            </div>
            <div className="relative flex justify-center text-xs uppercase tracking-widest">
              <span className="px-2" style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-muted)' }}>O continúa con</span>
            </div>
          </div>

          <button
            type="button"
            className="w-full h-11 btn-secondary"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google
          </button>

          <p className="text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}{' '}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="font-medium transition-colors"
              style={{ color: 'var(--color-blue-accent)' }}
              data-testid="toggle-auth-btn"
            >
              {isLogin ? 'Regístrate' : 'Inicia sesión'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Auth Callback Component for Google OAuth
export const AuthCallback = () => {
  const navigate = useNavigate();
  const { processGoogleSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const match = hash.match(/session_id=([^&]+)/);
      
      if (match) {
        const sessionId = match[1];
        try {
          const loggedUser = await processGoogleSession(sessionId);
          const sharedPath = sessionStorage.getItem('auth-return-path') || sessionStorage.getItem('asset-share-return') || '';
          sessionStorage.removeItem('auth-return-path');
          sessionStorage.removeItem('asset-share-return');
          navigate(safeReturnPath(sharedPath) || (['mediador', 'inversor'].includes(loggedUser?.role) ? '/activos' : '/dashboard'), { replace: true });
        } catch (error) {
          console.error('Google auth error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [navigate, processGoogleSession]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-primary)' }}>
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: 'var(--color-blue-accent)' }} />
        <p style={{ color: 'var(--color-text-muted)' }}>Autenticando...</p>
      </div>
    </div>
  );
};

export default LoginPage;
